import type { User } from '@crmsy/shared';
import type { ConnectionState } from '../lib/sync.js';
import type { View } from '../lib/router.js';
import { useT, type Key } from '../i18n/index.js';
import { useViewAs } from '../lib/viewAs.js';

export interface ShellProps {
  user: User;
  connection: ConnectionState;
  pending: number;
  view: View;
  onView: (v: View) => void;
  onLogout: () => void;
  onHelp: () => void;
  /** §20: Ask the CRM panel. */
  onAsk: () => void;
  /** D-36: admins only. */
  onViewAs: () => void;
  onBackToMe: () => void;
  search: string;
  setSearch: (q: string) => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
  children: React.ReactNode;
}

const NAV: { view: View; label: Key; key: string; icon: string }[] = [
  { view: 'new', label: 'nav.new', key: '1', icon: '＋' },
  { view: 'follow-ups', label: 'nav.followUps', key: '2', icon: '✓' },
  { view: 'table', label: 'nav.table', key: '3', icon: '☰' },
  { view: 'log', label: 'nav.log', key: '4', icon: '≡' },
];

/** One shell: left rail on desktop, bottom bar on phones (§6). */
export function Shell(p: ShellProps) {
  const t = useT();
  const viewAs = useViewAs();
  const viewing = !!viewAs && p.user.role === 'admin' && viewAs.userId !== p.user.id;
  const dot = p.connection === 'live' ? 'bg-ok' : p.connection === 'connecting' ? 'bg-warn-ink' : 'bg-danger';
  const label = p.connection === 'live' ? t('shell.live') : p.connection === 'connecting' ? t('shell.connecting') : t('shell.offline');
  return (
    <div className="flex h-full">
      <nav className="desktop-only flex w-52 shrink-0 flex-col border-r border-line bg-paper">
        <div className="flex items-center gap-2 px-4 py-4">
          <img src="/icon.svg" alt="" className="h-7 w-7 rounded-lg" />
          <span className="font-semibold">{t('app.title')}</span>
        </div>
        <div className="flex flex-col gap-0.5 px-2">
          {NAV.map((n) => (
            <button
              key={n.view}
              type="button"
              onClick={() => p.onView(n.view)}
              className={`flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${p.view === n.view ? 'bg-accent-soft font-semibold text-accent-dark' : 'hover:bg-surface'}`}
            >
              <span className="w-4 text-center">{n.icon}</span>
              <span className="flex-1">{t(n.label)}</span>
              <kbd>{n.key}</kbd>
            </button>
          ))}
        </div>
        <div className="mt-auto flex flex-col gap-1 border-t border-line px-2 py-3">
          <button type="button" onClick={() => p.onView('profile')} className={`rounded-lg px-2.5 py-1.5 text-left text-sm ${p.view === 'profile' ? 'bg-accent-soft text-accent-dark' : 'hover:bg-surface'}`}>
            {p.user.name}
          </button>
          {p.user.role === 'admin' && (
            <>
              <button type="button" onClick={() => p.onView('admin')} className={`rounded-lg px-2.5 py-1.5 text-left text-sm ${p.view === 'admin' ? 'bg-accent-soft text-accent-dark' : 'hover:bg-surface'}`}>
                {t('nav.admin')}
              </button>
              <button type="button" onClick={p.onViewAs} className={`rounded-lg px-2.5 py-1.5 text-left text-sm ${viewing ? 'bg-warn-soft text-warn-ink' : 'hover:bg-surface'}`} data-view-as>
                {viewing ? `👁 ${viewAs!.name}` : t('viewAs.button')}
              </button>
            </>
          )}
          <button type="button" onClick={p.onAsk} className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-surface" data-ask-button>
            <span>✨ {t('nav.ask')}</span> <kbd>q</kbd>
          </button>
          <button type="button" onClick={p.onHelp} className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-surface">
            {t('nav.shortcuts')} <kbd>?</kbd>
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className={`flex items-center gap-3 border-b border-line bg-paper px-4 py-2 ${p.view === 'new' ? 'max-md:hidden' : ''}`}>
          <div className="relative flex-1 md:max-w-md">
            <input
              ref={p.searchRef}
              value={p.search}
              onChange={(e) => p.setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  p.setSearch('');
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === 'Enter' || e.key === 'ArrowDown') (e.target as HTMLInputElement).blur();
              }}
              placeholder={t('shell.search')}
              className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 pr-8 text-sm outline-none focus:border-accent"
            />
            <kbd className="absolute right-2 top-1/2 -translate-y-1/2">/</kbd>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-muted">
            {p.pending > 0 && <span className="rounded-full bg-warn-soft px-2 py-0.5 font-medium text-warn-ink">{t('shell.pending', { n: p.pending })}</span>}
            <span className="inline-flex items-center gap-1.5" title={label}>
              <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
              <span className="desktop-only">{label}</span>
            </span>
            <button type="button" onClick={p.onLogout} className="desktop-only hover:text-ink">
              {t('nav.signOut')}
            </button>
          </div>
        </header>
        {viewing && (
          <div className="flex items-center gap-3 border-b border-line bg-warn-soft px-4 py-1.5 text-xs text-warn-ink" role="status" data-view-as-banner>
            <span className="flex-1">{t('viewAs.banner', { name: viewAs!.name })}</span>
            <button type="button" className="btn" onClick={p.onBackToMe}>
              {t('viewAs.back')}
            </button>
          </div>
        )}
        <main className="min-h-0 flex-1 overflow-hidden pb-14 md:pb-0">{p.children}</main>
      </div>

      <nav className="phone-only fixed inset-x-0 bottom-0 z-10 flex border-t border-line bg-paper">
        {NAV.map((n) => (
          <button
            key={n.view}
            type="button"
            onClick={() => p.onView(n.view)}
            className={`flex flex-1 flex-col items-center py-2 text-[11px] ${p.view === n.view ? 'text-accent-dark' : 'text-muted'}`}
          >
            <span className="text-base">{n.icon}</span>
            {t(n.label)}
          </button>
        ))}
        <button type="button" onClick={p.onAsk} className="flex flex-1 flex-col items-center py-2 text-[11px] text-muted">
          <span className="text-base">✨</span>
          {t('nav.ask')}
        </button>
        <button type="button" onClick={() => p.onView('profile')} className={`flex flex-1 flex-col items-center py-2 text-[11px] ${p.view === 'profile' ? 'text-accent-dark' : 'text-muted'}`}>
          <span className="text-base">☺</span>
          {t('nav.me')}
        </button>
      </nav>
    </div>
  );
}
